import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { IRuleTriggerTeamID } from "store/entities/rules/interfaces";
import { ITeam, selectTeams, selectTeamsAlphabetically } from "store/entities/teams";

import { changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerTeamID>;
}

export const TeamTrigger: FC<IProps> = ({ input }) => {
  const teamsById = useSelector(selectTeams);
  const teams = useSelector(selectTeamsAlphabetically);

  const equalityOptions = [createEqualityOption("is"), createEqualityOption("isNot")];

  const selectedEqualityOptions = [createEqualityOption(input.value.invert ? "isNot" : "is")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];
    const newInvert = selectedOption.key === "isNot";
    changeTriggerValues(input, { invert: newInvert });
  };

  const teamOptions = teams.map(team => createTeamOption(team));

  const selectedTeams = input.value.in.map(id => {
    const team = teamsById[id];
    return team ? createTeamOption(team) : createRemovedTeamOption(id);
  });

  const handleSelectTeam = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    changeTriggerValues(input, { in: selectedOptions.map(option => option.key) });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={teamOptions}
        selected={selectedTeams}
        onSelect={handleSelectTeam}
        placeholder="Select one team or more…"
      />
    </>
  );
};

const createEqualityOption = (key: "isNot" | "is"): IPickerListItem => ({
  key,
  name: key === "isNot" ? "is not" : "is",
});

const createTeamOption = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} isDefault={team.isDefault} pickerType="multi" />,
    listItemBody: <TeamPickerListItem name={team.name} isDefault={team.isDefault} pickerType="multi" />,
  },
});

const createRemovedTeamOption = (teamId: string): IPickerListItem => ({
  key: teamId,
  name: "Team removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
